import React from "react";
import classNames from "classnames";

import Conditional from "../../../common/conditional";
import CustomSection from "../../custom-general/custom-section";
import PDFTablePage from "../../../themed/pdf/pdfTablePage";
import ParseHTML from "../../../common/ParseHTML";
import PAUTConstraint from "../../custom-paut/paut-constraint";

import { Column } from "../../../../utils/models";
import { TOFDScanDataItem } from '../tofdScanDataItems';
import { getResultItemStyle } from "../../../../utils/data";

import styles from './style.module.scss';
import { getTotalScanLength } from "../../../../utils/utilities";

interface Props {
    tofdDataItems: TOFDScanDataItem[];
}

const TOFDData: React.FC<Props> = ({ tofdDataItems }) => {
    return ( 
        <>
            {tofdDataItems.map((tofdItem) => (
                <CustomSection key={tofdItem.weldNumber} sectionTitle="Result" sectionSubtitle="Scan Data">
                    <div className={classNames(["p-1", styles.summary])}>
                        <div className="container-fluid overflow-hidden">
                            <div className={classNames(["row", styles.summaryTitleContainer])}>
                                <div className={classNames(["col", styles.summaryTitle])}>
                                    <p>
                                        Weld Number
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle, styles.summaryTitleTender])}>
                                    <p>
                                        {tofdItem.weldNumber}
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle])}>
                                    <p>
                                        Scan Number
                                    </p>
                                </div>
                                <div className={classNames(["col-2 p-1", styles.summaryTitle, styles.summaryTitleTender, "border-0"])}>
                                    <p>
                                        {tofdItem.scanNumber}
                                    </p>
                                </div>
                            </div>

                            <div className={classNames(["row mt-1", styles.summaryTitleContainer])}>
                                <div className={classNames(["col-4 p-1", styles.summaryTitle])}>
                                    <p>
                                        Scan Angle (deg)
                                    </p>
                                </div>
                                <div className={classNames(["col-2 p-1", styles.summaryTitle, styles.summaryTitleTender])}>
                                    <p>
                                        {tofdItem.scanAngle}
                                    </p>
                                </div>
                                <div className={classNames(["col-4 p-1", styles.summaryTitle])}>
                                    <p>
                                        Minimum Measured Thickness (mm)
                                    </p>
                                </div>
                                <div className={classNames(["col-2 p-1", styles.summaryTitle, styles.summaryTitleTender, "border-0"])}>
                                    <p>
                                        {tofdItem.minimumMeasuredThickness}
                                    </p>
                                </div>
                            </div>
                            
                            <div className={classNames(["row mt-1", styles.summaryTitleContainer])}>
                                <div className={classNames(["col p-1", styles.summaryTitle])}>
                                    <p>
                                        Scan Start (mm)
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle, styles.summaryTitleTender])}>
                                    <p>
                                        {tofdItem.scanStart}
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle])}>
                                    <p>
                                        Scan End (mm)
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle, styles.summaryTitleTender])}>
                                    <p>
                                        {tofdItem.scanEnd}
                                    </p>
                                </div>
                                <div className={classNames(["col-3 p-1", styles.summaryTitle])}>
                                    <p>
                                        Total scan length (mm)
                                    </p>
                                </div>
                                <div className={classNames(["col p-1", styles.summaryTitle, styles.summaryTitleTender, "border-0"])}>
                                    <p>
                                        {getTotalScanLength(tofdItem.scanStart, tofdItem.scanEnd)}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <Conditional isVisible={tofdItem.aScan.length === 0 && tofdItem.sScan.length === 0}>
                            <PAUTConstraint />
                        </Conditional>

                        {tofdItem.aScan.length > 0 && (
                            <div className={classNames(["mt-1", styles.summaryScan])}>
                                <div className={classNames(["p-1", styles.summaryScanHeader])}>
                                    <p>A-SCAN</p>
                                </div>
                                <div className={styles.summaryScanBody}>
                                    <img src={tofdItem.aScan[0].src} alt={tofdItem.aScan[0].caption} />
                                </div>
                            </div>
                        )}
                        
                        {tofdItem.sScan.length > 0 && (
                            <div className={classNames(["mt-1", styles.summaryScan])}>
                                <div className={classNames(["p-1", styles.summaryScanHeader])}>
                                    <p>B-SCAN</p>
                                </div>
                                <div className={styles.summaryScanBody}>
                                    <img src={tofdItem.sScan[0].src} alt={tofdItem.sScan[0].caption} />
                                </div>
                            </div>
                        )}
                        
                        <div className="mt-1">
                            <PDFTablePage 
                                data={[tofdItem]} 
                                columns={COLUMNS} 
                            />
                        </div>

                        <div className="container-fluid mt-1">
                            <div className="row gap-1">
                                <div className={classNames(["col", styles.summaryContainer])}>
                                    <p className={styles.summaryContainerHeader}>
                                        LIMITATION (IF ANY):
                                    </p>
                                    <p>
                                        <ParseHTML text={tofdItem.limitation} />
                                    </p>
                                </div>
                                <div className={classNames(["col", styles.summaryContainer])}>
                                    <p className={styles.summaryContainerHeader}>
                                        REMARK (IF ANY):
                                    </p>
                                    <p>
                                        <ParseHTML text={tofdItem.remark} />
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </CustomSection>
            ))}
        </>
     );
};

const WARNING_STYLE = { 
    backgroundColor: '#ffff0f',
    color: '#000000'
};

const COLUMNS: Column[] = [
    {
        path: 'thickness',
        label: 'WELD THICKNESS (mm)',
        style: () => WARNING_STYLE
    },
    {
        path: 'pipeDia',
        label: 'PIPE DIA (inch)',
        style: () => WARNING_STYLE
    },
    {
        path: 'defectDepth',
        label: 'DEFECT DEPTH (mm)',
        style: () => WARNING_STYLE,
    },
    {
        path: 'defectLength',
        label: 'DEFECT LENGTH (mm)',
        style: () => WARNING_STYLE,
    },
    {
        path: 'defectHeight',
        label: 'DEFECT HEIGHT (mm)',
        style: () => WARNING_STYLE,
    },
    {
        path: 'defectType',
        label: 'DEFECT TYPE',
    },
    {
        path: 'result',
        label: 'RESULT',
        style: (item) => getResultItemStyle(item),
    },
];
 
export default TOFDData;